// Tooltip copy reused across KPI cards (MetricCard) and DataTable column
// headers (PlatformLeaderboard, LandingPageTable, CountryTable) so the
// explanation for e.g. "Conversions" stays identical wherever it's
// surfaced.
//
// `keyEventsInfo` takes the optional GA4 propertyId so the Conversions
// tooltip can deep-link to the user's actual GA4 Admin → Key events page.
// Falls back to the GA4 help article when the propertyId isn't known.

import type { ReactNode } from "react";

export const sessionsInfo: ReactNode = (
  <span>
    Total sessions from social traffic (Organic Social + Paid Social channel groups in GA4) over the
    selected period.
  </span>
);

export const landingPageInfo: ReactNode = (
  <span>
    Pulled from GA4's <em>landingPagePlusQueryString</em> dimension — the path + query string of the
    first page a visitor saw in that session. The open-in-new-tab link prepends GA4's{" "}
    <em>hostName</em> so multi-subdomain properties resolve to the exact URL the visitor hit.
  </span>
);

export const platformInfo: ReactNode = (
  <span>
    The canonical social platform (Instagram, TikTok, LinkedIn, …) inferred from GA4's session
    source, after normalizing common aliases. "Top platform" here is the single platform that drove
    the most sessions to that landing page.
  </span>
);

export const engagementRateInfo: ReactNode = (
  <span>
    Share of social sessions GA4 classes as <em>engaged</em> — at least 10 seconds, a conversion
    event, or two pageviews. Higher is better.
  </span>
);

export const revenueInfo: ReactNode = (
  <span>
    Total revenue attributed to social sessions via GA4's <em>totalRevenue</em> metric (purchase +
    in-app + ad revenue, after attribution).
  </span>
);

export const pagesPerSessionInfo: ReactNode = (
  <span>
    Average number of pages a social visitor opens per session — GA4&rsquo;s{" "}
    <em>screenPageViews / sessions</em> for the same Organic Social + Paid Social cohort. Higher
    means visitors dig deeper into the site after the first click.
  </span>
);

export const customConversionsInfo: ReactNode = (
  <span>
    Events you picked in{" "}
    <a
      href="/app/settings#conversion-events"
      className="underline decoration-white/60 underline-offset-2 hover:text-white focus-visible:text-white"
    >
      Settings → Conversion events
    </a>
    . Sums GA4 <em>eventCount</em> filtered to those event names across the same social sessions
    that drive the rest of this report.
  </span>
);

// M13.4 — ecommerce KPI tiles. Tooltips explain the GA4 source so a
// user reconciling against GA4 directly can find the same metric.
export const transactionsInfo: ReactNode = (
  <span>
    Total ecommerce purchases attributed to social sessions over the window — GA4&rsquo;s{" "}
    <em>ecommercePurchases</em> metric, summed across the new per-channel breakdown.
  </span>
);

export const averageOrderValueInfo: ReactNode = (
  <span>
    Total ecommerce revenue divided by total transactions for the social cohort — recomputed
    server-side over the window so the headline reads as an arithmetic average rather than an
    average-of-daily-averages.
  </span>
);

export function keyEventsInfo(propertyId?: string): ReactNode {
  const adminHref = propertyId
    ? `https://analytics.google.com/analytics/web/#/p${propertyId}/admin/keyevents`
    : "https://support.google.com/analytics/answer/12966437";
  return (
    <span>
      Counts every GA4 <em>key event</em> fired during a social session. The list of events that
      count as conversions is set in your GA4 property —{" "}
      <a
        href={adminHref}
        target="_blank"
        rel="noopener noreferrer"
        className="underline decoration-white/60 underline-offset-2 hover:text-white focus-visible:text-white"
      >
        open GA4 Admin → Key events
      </a>{" "}
      to change which events are tracked. Prefer a custom list?{" "}
      <a
        href="/app/settings#conversion-events"
        className="underline decoration-white/60 underline-offset-2 hover:text-white focus-visible:text-white"
      >
        Configure Custom conversions in Settings
      </a>
      .
    </span>
  );
}
